import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import MaterialTable from "@material-table/core";
import {TablePagination, Checkbox, IconButton, useTheme} from "@material-ui/core";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import {
  MaskedText,
  DatePicker,
  MaskedNumber,
} from "../../../controls";

import { useAircraft, useAddAircraft, useUpdateAircraft, useDeleteAircraft } from "../../../services/aircraft";
import AircraftRateList from "./AircraftRateList";
import AircraftMovements from "../AircraftMovements";
import AircraftStatus from "./AircraftStatus";
import AircraftMaintenance from "./AircraftMaintenance";
import {ColumnWidth} from "../../../blocks/ColumnHelper";
import AircraftMovementCaptureDialog from "./AircraftMovementCaptureDialog";
import AircraftMaxPayloadList from "./AircraftMaxPayloadsList";
import {FreighterLoading, FreighterUnloading} from "../../../icons";


const dateFormat = "DD/MM/YYYY";
const today = moment().toISOString();

const AircraftList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const [movementsDialog, setMovementsDialog] = useState({
    showMovements: false,
    aircraft: "",
  });
  const [captureMovementDialog, setCaptureMovementDialog] = useState({
    captureMovement: false,
    aircraft: "",
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddAircraft = useAddAircraft();
  const handleUpdateAircraft = useUpdateAircraft();
  const handleDeleteAircraft = useDeleteAircraft();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const aircraft = useAircraft({tableRef, queryClient, extraKeys});

  const getAircraftColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9]-",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": ".",
      "C": "[A-Z0-9-]"
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [

      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        cellStyle: { padding: "8px 2px 8px 12px" },
        headerStyle: { padding: "8px 2px 8px 12px" },
        title: "Tail Number",
        field: "aircraftTailNumber",
        editable: props.canEdit ? "onAdd" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"C".repeat(10)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={(value)=>{
              const aircraftTailNumber = value||'';
              const externalTailNumber = aircraftTailNumber.replace(/[-]/g, '');
              props.onRowDataChange({...props.rowData, aircraftTailNumber, externalTailNumber})
            }}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(60)),
        title: "Lessor Code",
        field: "aircraftLessor",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(2)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(50)),
        title: "Aircraft Type",
        field: "aircraftType",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(4)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Payload",
        field: "payload",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(6)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.payload}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "MTOW",
        field: "MTOW",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(6)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.MTOW}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "MZFW",
        field: "MZFW",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(6)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.MZFW}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "MLW",
        field: "MLW",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(6)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.MLW}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(60)),
        title: "Fuel Bias",
        field: "fuelBiasPercent",
        type: "numeric",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedNumber
            decimalScale={2}
            fixedDecimalScale={true}
            valueCap={100}
            suffix=" %"
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            decimalScale={2}
            fixedDecimalScale={true}
            valueCap={100}
            suffix=" %"
            displayType="text"
            value={props.fuelBiasPercent}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(60)),
        title: "Seats",
        field: "seats",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(3)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.seats}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(60)),
        title: "Main Deck",
        field: "mainDeckPallets",
        type: "numeric",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(3)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.mainDeckPallets}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(60)),
        title: "Lower Deck",
        field: "lowerDeckPallets",
        type: "numeric",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(3)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.lowerDeckPallets}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(20)),
        title: "",
        type: "numeric",
        editable: "never",
        initialEditValue: "",
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Manufactured",
        field: "manufactureDate",
        type: "date",
        emptyValue: today,
        initialEditValue:today,
        editComponent: (props) => (
            <DatePicker
              minDate={!props.value ? moment("1990-01-01", "YYYY-MM-DD") : undefined}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70)),
        title: "Active",
        field: "active",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.active === true} value="true" />
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70)),
        title: "Requires Permits",
        field: "permitsRequired",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.permitsRequired === true} value="true" />
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70)),
        title: "MX Interface",
        field: "maintenanceInterface",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.maintenanceInterface === true} value="true" />
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70)),
        title: "Delayed Fuel",
        field: "fuelLogMode",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.fuelLogMode === "DELAYED"} value="DELAYED"/>,
        editComponent: (props) => (
          <Checkbox
            style={{padding: 0}}
            checked={props.rowData.fuelLogMode === "DELAYED"}
            value="DELAYED"
            onChange={(e, cbValue)=>{
              const newValue = cbValue?"DELAYED":"STANDARD";
              props.onRowDataChange({...props.rowData, fuelLogMode: newValue})
            }}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70)),
        title: "Accept MVT",
        field: "acceptMvtMessages",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.acceptMvtMessages === true} value="true" />
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        cellStyle: { padding: "8px 2px 8px 12px" },
        headerStyle: { padding: "8px 2px 8px 12px" },
        title: "Jetplan Tail",
        field: "jetplanTailNumber",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"C".repeat(10)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            inputProps={defaultEditProps}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        title: "Notes",
        field: "notes",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Status",
        field: "aircraftTailNumber",
        align: "center",
        sorting: false,
        editable: "never",
        render: rowData =>
          {
            return (rowData.active
              ?
                <AircraftStatus
                  aircraftTailNumber={rowData.aircraftTailNumber}
                  height={20}
                  width={20}
                  //backgroundStyle={{fill: "#f0f0f0"}}
                  //offStyle={{fill: "#e0e0e0"}}
                />
              :
                <></>
            )
          }
      },
    ];
  };

  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
          tableRef={tableRef}
          style={{
            border: "none",
            boxShadow: "none",
            borderRadius: 0,
          }}
          options={{
            actionsColumnIndex: -1,
            cellStyle: {
              padding: "8px 2px",
            },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: "2px",
            },
            toolbarButtonAlignment: "right",
            pageSize: 10,
            detailPanelType: "single",
            padding: 'dense',
          }}
          actions={[
            (rowData) => (
              {
                hidden: false,
                disabled: !rowData.active,
                icon: 'track_changes',
                iconProps: { style: (rowData.active) ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}},
                tooltip: "Aircraft Movements",
                isFreeAction: false,
                onClick: (e, rowData) => {
                  setMovementsDialog({showMovements: true, aircraft: rowData.aircraftTailNumber});
                }
              }
            ),
            (rowData) => (
              {
                hidden: false,
                disabled: !rowData.active,
                icon: 'access_time',
                iconProps: { style: (rowData.active) ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}},
                tooltip: "Add Manual Aircraft Movements",
                isFreeAction: false,
                onClick: (e, rowData) => {
                  setCaptureMovementDialog({captureMovement: true, aircraft: rowData.aircraftTailNumber});
                }
              }
            ),
          ]}
          components={{
            ...defaultComponents,
            Pagination: (props) => (
                <TablePagination
                    {...props}
                    rowsPerPageOptions={[5, 10, 15]}
                />
            ),
          }}
          title="Aircraft Management"
          columns={getAircraftColumns({canEdit: state.canEdit})}
          detailPanel={
            [
              {
                tooltip: "Aircraft Rates",
                icon: 'monetization_on',
                openIcon: 'attach_money',
                render: (rowData) => {
                  return (
                    <div >
                      <div style={{ backgroundColor: "#f2f2f2" }}>
                        <AircraftRateList
                          canEdit={state.canEdit}
                          aircraftUuid={rowData.aircraftUuid}
                        />
                      </div>
                    </div>
                  );
                },
              },
              {
                tooltip: "AircraftMax Payloads",
                icon: () => { return(<FreighterUnloading style={{width: 24, height:24 }}/>)},
                openIcon: () => { return(<FreighterUnloading style={{width: 24, height:24 }}/>)},
                render: (rowData) => {
                  return (
                    <div >
                      <div style={{ backgroundColor: "#f2f2f2" }}>
                        <AircraftMaxPayloadList
                          canEdit={state.canEdit}
                          aircraftUuid={rowData.aircraftUuid}
                          aircraftTailNumber={rowData.aircraftTailNumber}
                        />
                      </div>
                    </div>
                  );
                },
              },
              {
                tooltip: "Scheduled Maintenance",
                icon: 'build',
                //openIcon: 'attach_money',
                // icon: () => {
                //   return(
                //     <AircraftMaintenance style={{width: 24, height:24 }}/>
                //   )
                // },
                render: (rowData) => {
                  return (
                    <div >
                      <div style={{ backgroundColor: "#f2f2f2" }}>
                        <AircraftMaintenance
                          canEdit={state.canEdit}
                          aircraftTailNumber={rowData.aircraftTailNumber}
                          externalAircraftTailNumber={rowData.externalTailNumber}
                          maintenanceInterface={rowData.maintenanceInterface}
                          aircraftUuid={rowData.aircraftUuid}
                        />
                      </div>
                    </div>
                  );
                },
              },
            ]
          }
          data={aircraft.data}
          editable={{
            isEditable: () => {
              return state.canEdit;
            },
            isDeletable: () => {
              return state.canEdit;
            },
            onRowAdd: state.canEdit
                ? (newData) => new Promise((resolve, reject) => {
                  handleAddAircraft.mutate({record: newData, resolve, reject, extraKeys});
                })
                : undefined,
            onRowUpdate: (newData, oldData) =>
                new Promise((resolve, reject) => {
                  handleUpdateAircraft.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                }),
            onRowDelete: (oldData) =>
                new Promise((resolve, reject) => {
                  handleDeleteAircraft.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                }),
          }}
        />
        {movementsDialog.showMovements
          ? <AircraftMovements
              title={movementsDialog.aircraft + " Movements"}
              open={movementsDialog.showMovements}
              aircraftTailNumber={movementsDialog.aircraft}
              onChange={() => {
                setMovementsDialog({showMovements: false, aircraft: ""});
              }}
              canEdit={state.canEdit}
            />
          : <></>
        }
        {captureMovementDialog.captureMovement
          ? <AircraftMovementCaptureDialog
              title={"Capture Movement for " + captureMovementDialog.aircraft}
              open={captureMovementDialog.captureMovement}
              aircraftTailNumber={captureMovementDialog.aircraft}
              onChange={() => {
                setCaptureMovementDialog({captureMovement: false, aircraft: ""});
              }}
            />
          : <></>
        }
      </div>
  );

}

export default AircraftList;
